import { CloudDownloadRounded, CloudUploadRounded } from '@mui/icons-material'
import { CircularProgress, IconButton, MenuItem, MenuList, Popover, Tooltip, Typography, useTheme } from '@mui/material'
import withStyles from '@mui/styles/withStyles'
import { AddUser, Save, Trash } from 'genesis-suite/icons'
import { ExportTypes } from 'genesis-suite/types/visualTypes'
import { forwardRef, useImperativeHandle, useState } from 'react'
import userRoles from '../../lib/userRoles'
import DataTable from '../DataTable'
import getUserColumns from './getUserColumns'

const styles = theme => ({
    table: {
        position: 'relative',
        display: 'flex',
        flex: '1 0 auto',
        flexFlow: 'column nowrap',
        paddingRight: 1,
        [theme.breakpoints.down('md')]: {
            margin: '.5rem auto 0 auto',
        },
        '& .filter-disabled': {
            flex: '0 0 auto',
            width: 60,
            textAlign: 'center',
            backgroundColor: '#e0e0e0',
            opacity: '.65',
            cursor: 'not-allowed',
        },
    },
    actionButton: {
        marginLeft: theme.spacing(1),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.primary.light,
        },
        '&$disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
        },
    },
    disabled: {},
    submitSpinner: {
        color: theme.palette.primary.light,
        position: 'relative',
        zIndex: 9,
    },
    showPointer: {
        cursor: 'pointer',
    },
})

const UserTable = forwardRef(
    (
        {
            classes,
            context,
            users,
            selectedUser,
            onChange,
            onSubmit,
            onShowAddUserModal,
            toggleUploadFile,
            onUserCheckbox,
            onUserFilterSelect,
            buttonState,
            onDeleteUsers,
            selectedUserList,
            handleSearch,
            exportUsers,
        },
        ref
    ) => {
        const theme = useTheme()
        const {
            palette: {
                primary: { contrastText },
            },
        } = theme

        const [anchorEl, setAnchorEl] = useState(null)

        const userExportId = Boolean(anchorEl) == true ? 'user-export' : undefined

        const handleExportBtnClick = event => {
            setAnchorEl(event.currentTarget)
        }

        const handleExportPopoverClose = () => {
            setAnchorEl(null)
        }

        useImperativeHandle(ref, () => ({
            handleExportPopoverClose,
        }))

        const handleChange = model => {
            const rowId = model.id
            const user = users.filter(row => rowId === row.id)[0]
            const val = Object.keys(userRoles).find(key => userRoles[key].toLowerCase() === model.value.toLowerCase())
            if (model.value != user[model.field]) {
                if (model.field === 'role') {
                    user.permissions = { ...user.permissions, [context]: val }
                    user.role = model.value
                } else {
                    user[model.field] = model.value
                }
                onChange(user)
            }
        }

        const saveBtn = (
            <Tooltip title="Save Changes">
                <IconButton
                    key="save-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    disabled={buttonState.disabled}
                    onClick={onSubmit}
                    size="large"
                >
                    {!buttonState.submitting ? (
                        <Save htmlColor={contrastText} />
                    ) : (
                        <CircularProgress size={24} className={classes.submitSpinner} />
                    )}
                </IconButton>
            </Tooltip>
        )

        const addUser = (
            <Tooltip title="Add User">
                <IconButton
                    key="add-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    onClick={onShowAddUserModal}
                    size="large"
                >
                    <AddUser htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )

        const deleteUsers = (
            <Tooltip title="Delete">
                <IconButton
                    key="delete-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    disabled={users && selectedUserList.length === 0}
                    onClick={onDeleteUsers}
                    size="large"
                >
                    <Trash htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )

        const uploadFile = (
            <Tooltip title="Add Multiple users">
                <IconButton
                    key="upload-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    onClick={toggleUploadFile}
                    size="large"
                >
                    <CloudUploadRounded htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )

        const downloadUsers = (
            <Tooltip title="Export users">
                <IconButton
                    key="upload-btn"
                    classes={{
                        root: classes.actionButton,
                        disabled: classes.disabled,
                    }}
                    disabled={users?.length === 0}
                    onClick={handleExportBtnClick}
                    size="large"
                >
                    <CloudDownloadRounded htmlColor={contrastText} />
                </IconButton>
            </Tooltip>
        )

        return (
            <>
                <DataTable
                    className={classes.table}
                    rows={users || []}
                    columns={getUserColumns(selectedUser, onUserFilterSelect, theme, classes)}
                    onSelectRow={onUserCheckbox}
                    title="Manage Users"
                    extraActions={[addUser, saveBtn, deleteUsers, uploadFile, downloadUsers]}
                    onEdit={handleChange}
                    onSearch={handleSearch}
                />
                <Popover
                    id={userExportId}
                    open={Boolean(userExportId)}
                    anchorEl={anchorEl}
                    onClose={handleExportPopoverClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuList>
                        <MenuItem onClick={() => exportUsers(ExportTypes.Excel)}>
                            <Typography>Export as Excel</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => exportUsers(ExportTypes.PDF)}>
                            <Typography>Export as PDF</Typography>
                        </MenuItem>
                    </MenuList>
                </Popover>
            </>
        )
    }
)

export default withStyles(styles)(UserTable)
